import type {
  AnyVariables,
  CombinedError,
  OperationContext,
  OperationResult,
  TypedDocumentNode,
} from '@urql/core';

import { SILENT_ERRORS_CONTEXT, urqlClient } from '@shared/api';
import { getStoredUser } from '@shared/auth/auth-route';
import { getTokenClaims } from '@shared/auth/token.store';
import { gql } from '@shared/types/gql';

import { AiDataPolicyForbiddenError, AiDataPolicyHistoryForbiddenError } from './errors';
import { actorName } from './names';
import {
  AI_DATA_POLICY_CHANGES_PAGE_SIZE,
  type AiDataPolicy,
  type AiDataPolicyChange,
  type AiDataPolicyChangesPage,
  type AiDataPolicyChoice,
  type AiDataPolicyService,
  type AiDataPolicyValue,
  type AiDataPolicyWorkspace,
  type SetAiDataPolicyResult,
} from './types';

export type ApiAiDataPolicyValue = 'INTERNAL_ONLY' | 'COMMERCIAL_ALLOWED';

export interface ApiTenantAiDataPolicy {
  policy: ApiAiDataPolicyValue;
  decided: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface AiDataPolicyData {
  aiDataPolicy: ApiTenantAiDataPolicy;
}

interface SetAiDataPolicyData {
  setAiDataPolicy: {
    changed: boolean;
    errors: { message: string }[] | null;
    item: ApiTenantAiDataPolicy | null;
  };
}

type SetAiDataPolicyVars = {
  input: { policy: ApiAiDataPolicyValue; onlyIfUndecided: boolean };
};

interface AiDataPolicyHistoryData {
  aiDataPolicy: ApiTenantAiDataPolicy;
  permissionAuditLog: {
    list: {
      items: { id: string; createdAt: string }[];
      pageInfo: { page: number; totalItems: number; hasNextPage: boolean };
    };
  };
}

type AiDataPolicyHistoryVars = { page: number; perPage: number };

interface UserSelectData {
  security: {
    users: {
      selectListView: {
        items: { id: string; displayName: string }[];
        totalCount: number;
      };
    } | null;
  } | null;
}

type UserSelectVars = {
  limit: number;
  filter?: { userIds?: string[]; roles?: string[]; includeDeleted?: boolean };
};

export const AI_DATA_POLICY_QUERY: TypedDocumentNode<AiDataPolicyData, Record<string, never>> = gql`
  query AiDataPolicy {
    aiDataPolicy {
      policy
      decided
      updatedBy
      updatedAt
    }
  }
`;

export const SET_AI_DATA_POLICY_MUTATION: TypedDocumentNode<
  SetAiDataPolicyData,
  SetAiDataPolicyVars
> = gql`
  mutation SetAiDataPolicy($input: SetAiDataPolicyInput!) {
    setAiDataPolicy(input: $input) {
      changed
      errors {
        message
      }
      item {
        policy
        decided
        updatedBy
        updatedAt
      }
    }
  }
`;

export const AI_DATA_POLICY_HISTORY_QUERY: TypedDocumentNode<
  AiDataPolicyHistoryData,
  AiDataPolicyHistoryVars
> = gql`
  query AiDataPolicyHistory($page: Int!, $perPage: Int!) {
    aiDataPolicy {
      policy
      decided
      updatedBy
      updatedAt
    }
    permissionAuditLog {
      list(action: UPDATE_AI_DATA_POLICY, targetType: TENANT, page: $page, perPage: $perPage) {
        items {
          id
          createdAt
        }
        pageInfo {
          page
          totalItems
          hasNextPage
        }
      }
    }
  }
`;

export const AI_DATA_POLICY_USERS_QUERY: TypedDocumentNode<UserSelectData, UserSelectVars> = gql`
  query AiDataPolicyUsers($limit: Int, $filter: UserSelectFilterInput) {
    security {
      users {
        selectListView(limit: $limit, filter: $filter) {
          items {
            id
            displayName
          }
          totalCount
        }
      }
    }
  }
`;

type GqlResult<Data, Vars extends AnyVariables> = Pick<
  OperationResult<Data, Vars>,
  'data' | 'error'
>;

export interface AiDataPolicyGqlClient {
  query<Data, Vars extends AnyVariables>(
    doc: TypedDocumentNode<Data, Vars>,
    vars: Vars,
    context?: Partial<OperationContext>,
  ): { toPromise(): Promise<GqlResult<Data, Vars>> };
  mutation<Data, Vars extends AnyVariables>(
    doc: TypedDocumentNode<Data, Vars>,
    vars: Vars,
    context?: Partial<OperationContext>,
  ): { toPromise(): Promise<GqlResult<Data, Vars>> };
}

export interface GraphqlAiDataPolicyDeps {
  client: AiDataPolicyGqlClient;
  currentUser: () => { id: string; firstName?: string | null; lastName?: string | null } | null;
  channel: () => BroadcastChannel | null;
  tenant: () => string | null;
}

export const AI_DATA_POLICY_CHANNEL = 'investbanq-ai-data-policy';

const MAX_ADMIN_NAMES = 5;

const SILENT = { requestPolicy: 'network-only', ...SILENT_ERRORS_CONTEXT } as const;

function openChannel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel(AI_DATA_POLICY_CHANNEL);
  } catch {
    return null;
  }
}

export function isForbidden(error: CombinedError | undefined): boolean {
  if (!error) return false;
  if (error.response && (error.response as { status?: number }).status === 403) return true;
  return error.graphQLErrors.some((e) => {
    const ext = e.extensions;
    return (
      ext['code'] === 'FORBIDDEN' ||
      ext['statusCode'] === 403 ||
      ext['error'] === 'PERMISSION_DENIED' ||
      /forbidden|access denied|permission/i.test(e.message)
    );
  });
}

export function valueOf(api: Pick<ApiTenantAiDataPolicy, 'policy' | 'decided'>): AiDataPolicyValue {
  if (!api.decided) return 'not_chosen';
  return api.policy === 'COMMERCIAL_ALLOWED' ? 'commercial_allowed' : 'investbanq_only';
}

export function apiChoice(value: AiDataPolicyChoice): ApiAiDataPolicyValue {
  return value === 'commercial_allowed' ? 'COMMERCIAL_ALLOWED' : 'INTERNAL_ONLY';
}

function other(value: AiDataPolicyChoice): AiDataPolicyChoice {
  return value === 'commercial_allowed' ? 'investbanq_only' : 'commercial_allowed';
}

const STAMP_TOLERANCE_MS = 5 * 60 * 1000;

export function deriveChanges(
  current: Pick<ApiTenantAiDataPolicy, 'policy' | 'decided' | 'updatedAt'>,
  rows: { id: string; createdAt: string }[],
  offset: number,
  total: number,
): AiDataPolicyChange[] {
  const value = valueOf(current);
  const unknown = rows.map((row) => ({
    id: row.id,
    fromValue: null,
    toValue: null,
    createdAt: row.createdAt,
  }));
  if (value === 'not_chosen') return unknown;
  const newest = offset === 0 ? rows[0] : undefined;
  if (newest && current.updatedAt) {
    const gap = Math.abs(Date.parse(newest.createdAt) - Date.parse(current.updatedAt));
    if (!(gap <= STAMP_TOLERANCE_MS)) return unknown;
  }
  return rows.map((row, i) => {
    const g = offset + i;
    const toValue = g % 2 === 0 ? value : other(value);
    const fromValue: AiDataPolicyValue = g >= total - 1 ? 'not_chosen' : other(toValue);
    return { id: row.id, fromValue, toValue, createdAt: row.createdAt };
  });
}

export function createGraphqlAiDataPolicyService(
  overrides: Partial<GraphqlAiDataPolicyDeps> = {},
): AiDataPolicyService {
  const deps: GraphqlAiDataPolicyDeps = {
    client: urqlClient,
    currentUser: () => {
      const user = getStoredUser();
      return user ? { id: user.id, firstName: user.firstName, lastName: user.lastName } : null;
    },
    channel: openChannel,
    tenant: () => getTokenClaims()?.tenantId ?? null,
    ...overrides,
  };
  const names = new Map<string, string | null>();
  let channel: BroadcastChannel | null | undefined;
  const listeners = new Set<(policy: AiDataPolicy) => void>();

  const channelOnce = () => {
    if (channel === undefined) {
      channel = deps.channel();
      if (channel) {
        channel.onmessage = (event: MessageEvent<unknown>) => {
          const data = event.data as { policy?: AiDataPolicy; tenant?: string | null } | null;
          const tenant = deps.tenant();
          if (!data?.policy || !tenant || data.tenant !== tenant) return;
          for (const listener of listeners) listener(data.policy);
        };
      }
    }
    return channel;
  };

  async function nameOf(userId: string): Promise<string | null> {
    if (names.has(userId)) return names.get(userId) ?? null;
    const me = deps.currentUser();
    if (me && me.id === userId) {
      const own = actorName([me.firstName, me.lastName].filter(Boolean).join(' '), 'first-last');
      if (own) {
        names.set(userId, own);
        return own;
      }
    }
    try {
      const result = await deps.client
        .query(
          AI_DATA_POLICY_USERS_QUERY,
          { limit: 1, filter: { userIds: [userId], includeDeleted: true } },
          SILENT,
        )
        .toPromise();
      const item = result.data?.security?.users?.selectListView.items.find((u) => u.id === userId);
      const name = item ? actorName(item.displayName, 'last-first') : null;
      if (!result.error) names.set(userId, name);
      return name;
    } catch {
      return null;
    }
  }

  async function toPolicy(api: ApiTenantAiDataPolicy): Promise<AiDataPolicy> {
    const value = valueOf(api);
    if (value === 'not_chosen') return { value, setBy: null, setAt: null };
    return {
      value,
      setBy: api.updatedBy ? { id: api.updatedBy, name: await nameOf(api.updatedBy) } : null,
      setAt: api.updatedAt,
    };
  }

  return {
    async getPolicy() {
      const result = await deps.client.query(AI_DATA_POLICY_QUERY, {}, SILENT).toPromise();
      if (result.error || !result.data) {
        throw result.error ?? new Error('ai_data_policy_unavailable');
      }
      return toPolicy(result.data.aiDataPolicy);
    },

    async setPolicy(input): Promise<SetAiDataPolicyResult> {
      const result = await deps.client
        .mutation(
          SET_AI_DATA_POLICY_MUTATION,
          { input: { policy: apiChoice(input.value), onlyIfUndecided: input.onlyIfUndecided } },
          SILENT_ERRORS_CONTEXT,
        )
        .toPromise();
      if (isForbidden(result.error)) throw new AiDataPolicyForbiddenError();
      const payload = result.data?.setAiDataPolicy;
      if (result.error || !payload?.item) {
        throw (
          result.error ?? new Error(payload?.errors?.[0]?.message ?? 'ai_data_policy_not_saved')
        );
      }
      const policy = await toPolicy(payload.item);
      if (payload.changed) channelOnce()?.postMessage({ policy, tenant: deps.tenant() });
      return { changed: payload.changed, policy };
    },

    async listChanges(page): Promise<AiDataPolicyChangesPage> {
      const perPage = AI_DATA_POLICY_CHANGES_PAGE_SIZE;
      const result = await deps.client
        .query(AI_DATA_POLICY_HISTORY_QUERY, { page, perPage }, SILENT)
        .toPromise();
      if (isForbidden(result.error)) throw new AiDataPolicyHistoryForbiddenError();
      if (result.error || !result.data) {
        throw result.error ?? new Error('ai_data_policy_history_unavailable');
      }
      const { aiDataPolicy, permissionAuditLog } = result.data;
      const { items, pageInfo } = permissionAuditLog.list;
      return {
        items: deriveChanges(aiDataPolicy, items, (page - 1) * perPage, pageInfo.totalItems),
        total: pageInfo.totalItems,
        page: pageInfo.page,
        hasNextPage: pageInfo.hasNextPage,
      };
    },

    async getWorkspace(): Promise<AiDataPolicyWorkspace> {
      const [people, admins] = await Promise.all([
        deps.client
          .query(AI_DATA_POLICY_USERS_QUERY, { limit: 1 }, SILENT)
          .toPromise()
          .catch(() => null),
        deps.client
          .query(
            AI_DATA_POLICY_USERS_QUERY,
            { limit: MAX_ADMIN_NAMES, filter: { roles: ['ADMIN'] } },
            SILENT,
          )
          .toPromise()
          .catch(() => null),
      ]);
      const peopleView = people?.error ? null : people?.data?.security?.users?.selectListView;
      const adminView = admins?.error ? null : admins?.data?.security?.users?.selectListView;
      return {
        peopleCount: peopleView ? peopleView.totalCount : null,
        admins: adminView
          ? {
              names: adminView.items
                .map((u) => actorName(u.displayName, 'last-first'))
                .filter((n): n is string => Boolean(n)),
              total: adminView.totalCount,
            }
          : null,
      };
    },

    subscribe(listener) {
      listeners.add(listener);
      channelOnce();
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
